import { useState } from "react";
import moment from "moment-jalaali";
import { useSelector } from "react-redux";
import { ProgressBar } from "react-loader-spinner";
import { deleteChatbotRoom } from "../services/clientChatBotService";
import { confirmToast, showToast } from "toast/toastHelper";
import { 
  Trash2, 
  ChevronLeft, 
  ChevronRight, 
  MessageSquare,
  Calendar,
  User,
  Hash,
  Search
} from "lucide-react";

const ClientChatConversion = ({
  conversations,
  onConversationClick,
  title,
  selectedMenu,
  selectedItem,
  username,
  loading,
  onConversationDelete
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const accessToken = useSelector((state) => state.auth.accessToken);
  const name = useSelector((state) => state?.botInfo?.name);

  // فیلتر بر اساس جستجو
  const filteredConversations = conversations.filter((conv) =>
    conv.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredConversations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;

  // مرتب‌سازی بر اساس آخرین بروزرسانی
  const sortedConversations = [...filteredConversations].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
  );

  const selectedConversations = sortedConversations.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const handleDelete = async (conversation) => {
    try {
      setIsDeleting(true);

      const result = await deleteChatbotRoom({
        accessToken,
        roomId: conversation.id,
      });

      if (result && result.success !== false) {
        showToast("گفتگو حذف شد", "success");
        onConversationDelete?.(conversation.id);
      } else {
        showToast("خطا در حذف گفتگو", "error");
      }

    } catch (error) {
      console.error("خطا در حذف گفتگو:", error);
      let errorMessage = "خطا در حذف گفتگو";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showToast(errorMessage, "error");
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <ProgressBar
            visible={true}
            height="60"
            width="60"
            color="#1B4748"
            ariaLabel="progress-bar-loading"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
            در حال بارگذاری گفتگوها...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-zinc-800/90 rounded-2xl shadow-xl border border-gray-100/50 dark:border-zinc-700/50 overflow-hidden backdrop-blur-sm">
        
        {/* هدر کارت با جستجو */}
        <div className="px-6 py-5 border-b border-gray-100/80 dark:border-zinc-700/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-gray-50/50 to-white dark:from-zinc-800 dark:to-zinc-800/80">
          <div className="flex items-start w-full gap-3">
            <div className="p-2 rounded-xl bg-[#aadec5]/30 text-[#1B4748]">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h6 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {title || "لیست گفتگوها"}
              </h6>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {conversations.length} گفتگو
              </p>
            </div>
          </div>

          {/* جستجو */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="جستجوی گفتگو..."
              className="w-full pr-10 pl-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50/50 dark:bg-zinc-800/50 focus:border-[#1B4748] focus:ring-2 focus:ring-[#1B4748]/20 transition-all duration-200 outline-none"
            />
          </div>
        </div>

        {/* جدول */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-50/80 dark:bg-zinc-800/80">
              <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="px-6 py-3.5 w-16">
                  <div className="flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" />
                    شماره
                  </div>
                </th>
                <th className="px-6 py-3.5">
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    نام گفتگو
                  </div>
                </th>
                <th className="px-6 py-3.5">
                  <div className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    فرستنده
                  </div>
                </th>
                <th className="px-6 py-3.5">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    تاریخ
                  </div>
                </th>
                <th className="px-6 py-3.5 w-24 text-center">عملیات</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100/60 dark:divide-zinc-700/50">
              {selectedConversations?.length > 0 ? (
                selectedConversations.map((conversation, index) => (
                  <tr
                    key={conversation.id}
                    onClick={() => !isDeleting && onConversationClick(conversation)}
                    className="group hover:bg-[#aadec5]/10 dark:hover:bg-[#aadec5]/5 transition-all duration-200 cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100/70 dark:bg-zinc-700/50 text-xs font-medium">
                        {startIndex + index + 1}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#aadec5]/30 to-[#1B4748]/10 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-4 h-4 text-[#1B4748]" />
                        </div>
                        <span className="font-medium text-gray-800 dark:text-gray-200 line-clamp-1">
                          {conversation.name}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="text-gray-700 dark:text-gray-300">
                        {name?.trim() || username || ""}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {moment
                            .utc(conversation.updatedAt)
                            .local()
                            .locale("fa")
                            .format("jYYYY/jMM/jDD HH:mm")}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDeleting) {
                            confirmToast(() => handleDelete(conversation));
                          }
                        }}
                        disabled={isDeleting}
                        className={`p-2 rounded-xl transition-all duration-200 group-hover:scale-110
                          ${
                            isDeleting
                              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                              : "text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-400 dark:text-gray-500">
                        {searchTerm ? "گفتگویی با این نام یافت نشد" : "هیچ گفتگویی وجود ندارد"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* صفحه‌بندی */}
        {selectedConversations?.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100/80 dark:border-zinc-700/50 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50/30 dark:bg-zinc-800/30">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">تعداد در صفحه:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-gray-200 dark:border-zinc-700 rounded-lg px-5 py-1.5 bg-white dark:bg-zinc-800 focus:border-[#1B4748] focus:ring-1 focus:ring-[#1B4748]/20 outline-none transition-all"
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                صفحه {currentPage} از {totalPages || 1}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-xl transition-all duration-200 flex items-center gap-1
                  ${
                    currentPage === 1
                      ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                      : "text-gray-600 hover:bg-[#aadec5]/30 hover:text-[#1B4748] dark:text-gray-400 dark:hover:text-white"
                  }`}
              >
                <ChevronRight className="w-4 h-4" />
                <span className="text-sm">قبلی</span>
              </button>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-xl transition-all duration-200 flex items-center gap-1
                  ${
                    currentPage === totalPages
                      ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                      : "text-gray-600 hover:bg-[#aadec5]/30 hover:text-[#1B4748] dark:text-gray-400 dark:hover:text-white"
                  }`}
              >
                <span className="text-sm">بعدی</span>
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* لودر حذف */}
      {isDeleting && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#1B4748] rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              در حال حذف گفتگو...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientChatConversion;